// The Stock screens' server state (spec/13 §6.4, ADR-0040), as TanStack Query hooks over the generated client —
// `packages/contracts/src/routes/inventory.ts`. Every route is venue-scoped:
//
//   GET  …/inventory/status | alerts                    inventory:read     is stock on, what needs someone
//   GET  …/inventory/items, …/items/{id}, …/lookup       inventory:read     the list, one item, a scanned barcode
//   POST …/inventory/items, PATCH …/items/{id}           inventory:manage   (inventory:supplies for a supply)
//   GET  …/inventory/movements                           inventory:read     an item's history, cursor-paginated
//   GET/POST …/inventory/suppliers, PATCH …/{id}         read / manage
//   GET/POST …/inventory/receipts, GET …/receipts/{id}   read / receive     deliveries (Idempotency-Key)
//   POST …/inventory/usage | adjustments                 waste / adjust     (Idempotency-Key)
//   GET/POST …/inventory/counts, GET …/counts/{id}, PUT …/lines, POST …/submit | cancel     read / count
//   GET/PUT …/inventory/products/{p}/recipes             read / manage      a product's recipes here
//   POST …/inventory/copy-from-venue                     inventory:manage
//   GET  …/inventory/reports/valuation | usage | variance | margins          inventory:reports
//
// The ₹ figures on the reads are `null` without `inventory:reports`; the screens show "—" then. A write changes what
// is on hand, so it invalidates the whole venue's stock (`stockKeys.venue`): the list, the status, the alerts, the
// item's history and the reports all re-read.

import type {
  CopyStockFromVenueRequest,
  CreateStockAdjustmentRequest,
  CreateStockCountRequest,
  CreateStockItemRequest,
  CreateStockReceiptRequest,
  CreateStockSupplierRequest,
  CreateStockUsageRequest,
  PutStockCountLinesRequest,
  PutStockProductRecipesRequest,
  StockItemKind,
  StockLevel,
  StockMovementKind,
  UpdateStockItemRequest,
  UpdateStockSupplierRequest,
} from '@clavian/api-client';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { sellKeys } from '../sell/queries';

/** The page the history and the lists read at a time. */
const PAGE_SIZE = 50;

export const stockKeys = {
  /** Everything about one venue's stock: what a write invalidates. */
  venue: (venueId: string) => ['stock', venueId] as const,
  status: (venueId: string) => ['stock', venueId, 'status'] as const,
  alerts: (venueId: string) => ['stock', venueId, 'alerts'] as const,
  items: (venueId: string, includeArchived: boolean) => ['stock', venueId, 'items', includeArchived] as const,
  item: (venueId: string, stockItemId: string) => ['stock', venueId, 'item', stockItemId] as const,
  movements: (venueId: string, filter: MovementFilter) => ['stock', venueId, 'movements', filter] as const,
  suppliers: (venueId: string, includeArchived: boolean) => ['stock', venueId, 'suppliers', includeArchived] as const,
  receipts: (venueId: string) => ['stock', venueId, 'receipts'] as const,
  receipt: (venueId: string, receiptId: string) => ['stock', venueId, 'receipt', receiptId] as const,
  counts: (venueId: string) => ['stock', venueId, 'counts'] as const,
  count: (venueId: string, countId: string) => ['stock', venueId, 'count', countId] as const,
  recipes: (venueId: string) => ['stock', venueId, 'recipes'] as const,
  productRecipes: (venueId: string, productId: string) => ['stock', venueId, 'product-recipes', productId] as const,
  report: (venueId: string, report: string, from?: string, to?: string) => ['stock', venueId, 'report', report, from ?? null, to ?? null] as const,
};

/** Whether stock is tracked at the venue, and how many items are low, out or below zero. `inventory:read`. */
export function useStockStatus(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: stockKeys.status(venueId),
    queryFn: () => api.getStockStatus({ path: { organizationId, venueId } }),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

/** The low, out and negative items and the dishes sold out by stock — "What needs you". `inventory:read`. */
export function useStockAlerts(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: stockKeys.alerts(venueId),
    queryFn: () => api.getStockAlerts({ path: { organizationId, venueId } }),
    enabled,
    retry: false,
    refetchInterval: 60_000,
  });
}

/** Every item at the venue (archived ones too, on request), by name. Filtering happens on the screen. */
export function useStockItems(organizationId: string, venueId: string, options: { readonly includeArchived?: boolean; readonly enabled?: boolean } = {}) {
  const api = useApi();
  const includeArchived = options.includeArchived ?? false;
  return useQuery({
    queryKey: stockKeys.items(venueId, includeArchived),
    queryFn: async () => (await api.listStockItems({ path: { organizationId, venueId }, query: includeArchived ? { includeArchived: 'true' } : {} })).items,
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useStockItem(organizationId: string, venueId: string, stockItemId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: stockKeys.item(venueId, stockItemId ?? 'none'),
    queryFn: () => api.getStockItem({ path: { organizationId, venueId, stockItemId: stockItemId ?? '' } }),
    enabled: stockItemId !== null,
  });
}

/** Resolve a scanned barcode to its item (and pack). Not cached: each scan asks. */
export function useLookupBarcode(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation({ mutationFn: (barcode: string) => api.lookupStockBarcode({ path: { organizationId, venueId }, query: { barcode } }) });
}

export interface MovementFilter {
  readonly stockItemId?: string;
  readonly kind?: StockMovementKind;
  /** The venue's business days, both inclusive, `YYYY-MM-DD`. */
  readonly fromDay?: string;
  readonly toDay?: string;
}

/** The movement ledger, newest first, a page at a time. */
export function useStockMovements(organizationId: string, venueId: string, filter: MovementFilter, enabled = true) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: stockKeys.movements(venueId, filter),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.listStockMovements({
        path: { organizationId, venueId },
        query: {
          limit: PAGE_SIZE,
          ...(filter.stockItemId === undefined ? {} : { stockItemId: filter.stockItemId }),
          ...(filter.kind === undefined ? {} : { kind: filter.kind }),
          ...(filter.fromDay === undefined ? {} : { fromDay: filter.fromDay }),
          ...(filter.toDay === undefined ? {} : { toDay: filter.toDay }),
          ...(pageParam === undefined ? {} : { cursor: pageParam }),
        },
      }),
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

export function useStockSuppliers(organizationId: string, venueId: string, options: { readonly includeArchived?: boolean; readonly enabled?: boolean } = {}) {
  const api = useApi();
  const includeArchived = options.includeArchived ?? false;
  return useQuery({
    queryKey: stockKeys.suppliers(venueId, includeArchived),
    queryFn: async () => (await api.listStockSuppliers({ path: { organizationId, venueId }, query: includeArchived ? { includeArchived: 'true' } : {} })).suppliers,
    enabled: options.enabled ?? true,
  });
}

export function useStockReceipts(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: stockKeys.receipts(venueId),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.listStockReceipts({ path: { organizationId, venueId }, query: { limit: PAGE_SIZE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

export function useStockReceipt(organizationId: string, venueId: string, receiptId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: stockKeys.receipt(venueId, receiptId ?? 'none'),
    queryFn: () => api.getStockReceipt({ path: { organizationId, venueId, receiptId: receiptId ?? '' } }),
    enabled: receiptId !== null,
  });
}

export function useStockCounts(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: stockKeys.counts(venueId),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.listStockCounts({ path: { organizationId, venueId }, query: { limit: PAGE_SIZE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

export function useStockCount(organizationId: string, venueId: string, countId: string) {
  const api = useApi();
  return useQuery({ queryKey: stockKeys.count(venueId, countId), queryFn: () => api.getStockCount({ path: { organizationId, venueId, countId } }) });
}

export function useProductRecipes(organizationId: string, venueId: string, productId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: stockKeys.productRecipes(venueId, productId),
    queryFn: () => api.getStockProductRecipes({ path: { organizationId, venueId, productId } }),
    enabled,
  });
}

/** Every recipe at the venue — which products have one, for "by dish" usage. */
export function useStockRecipes(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: stockKeys.recipes(venueId),
    queryFn: async () => (await api.listStockRecipes({ path: { organizationId, venueId } })).recipes,
    enabled,
  });
}

// -- reports (inventory:reports) ---------------------------------------------------------------

export function useValuationReport(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: stockKeys.report(venueId, 'valuation'), queryFn: () => api.getStockValuationReport({ path: { organizationId, venueId } }), enabled, retry: false });
}

export function useUsageReport(organizationId: string, venueId: string, from: string, to: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: stockKeys.report(venueId, 'usage', from, to),
    queryFn: () => api.getStockUsageReport({ path: { organizationId, venueId }, query: { from, to } }),
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

export function useVarianceReport(organizationId: string, venueId: string, from: string, to: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: stockKeys.report(venueId, 'variance', from, to),
    queryFn: () => api.getStockVarianceReport({ path: { organizationId, venueId }, query: { from, to } }),
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

export function useMarginsReport(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: stockKeys.report(venueId, 'margins'), queryFn: () => api.getStockMarginsReport({ path: { organizationId, venueId } }), enabled, retry: false });
}

// -- writes ------------------------------------------------------------------------------------

/** After any write: everything about the venue's stock re-reads, and Sell's menu (a dish may be sold out now). */
function useInvalidateStock(organizationId: string, venueId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: stockKeys.venue(venueId) }),
      queryClient.invalidateQueries({ queryKey: sellKeys.menu(organizationId, venueId) }),
    ]);
}

export function useCreateStockItem(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({ mutationFn: (body: CreateStockItemRequest) => api.createStockItem({ path: { organizationId, venueId }, body }), onSuccess: invalidate });
}

export function useUpdateStockItem(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({
    mutationFn: ({ stockItemId, body }: { stockItemId: string; body: UpdateStockItemRequest }) => api.updateStockItem({ path: { organizationId, venueId, stockItemId }, body }),
    onSuccess: invalidate,
  });
}

export function useCreateSupplier(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({ mutationFn: (body: CreateStockSupplierRequest) => api.createStockSupplier({ path: { organizationId, venueId }, body }), onSuccess: invalidate });
}

export function useUpdateSupplier(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({
    mutationFn: ({ supplierId, body }: { supplierId: string; body: UpdateStockSupplierRequest }) => api.updateStockSupplier({ path: { organizationId, venueId, supplierId }, body }),
    onSuccess: invalidate,
  });
}

/**
 * Receive a delivery. The caller keeps one `Idempotency-Key` for as long as it sends the same delivery, so a retry
 * after a dropped answer is the receipt already made, never a second delivery.
 */
export function useCreateReceipt(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({
    mutationFn: ({ body, key }: { body: CreateStockReceiptRequest; key: string }) => api.createStockReceipt({ path: { organizationId, venueId }, headerParams: { 'idempotency-key': key }, body }),
    onSuccess: invalidate,
  });
}

export function useRecordUsage(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({
    mutationFn: ({ body, key }: { body: CreateStockUsageRequest; key: string }) => api.recordStockUsage({ path: { organizationId, venueId }, headerParams: { 'idempotency-key': key }, body }),
    onSuccess: invalidate,
  });
}

export function useCreateAdjustment(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({
    mutationFn: ({ body, key }: { body: CreateStockAdjustmentRequest; key: string }) => api.createStockAdjustment({ path: { organizationId, venueId }, headerParams: { 'idempotency-key': key }, body }),
    onSuccess: invalidate,
  });
}

export function useCreateCount(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({ mutationFn: (body: CreateStockCountRequest) => api.createStockCount({ path: { organizationId, venueId }, body }), onSuccess: invalidate });
}

/** Enter counted figures on an open count; the answer is the count as it now stands, written straight to its cache. */
export function usePutCountLines(organizationId: string, venueId: string, countId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PutStockCountLinesRequest) => api.putStockCountLines({ path: { organizationId, venueId, countId }, body }),
    onSuccess: (count) => queryClient.setQueryData(stockKeys.count(venueId, countId), count),
  });
}

export function useSubmitCount(organizationId: string, venueId: string, countId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({
    mutationFn: ({ key, note }: { key: string; note?: string }) =>
      api.submitStockCount({ path: { organizationId, venueId, countId }, headerParams: { 'idempotency-key': key }, body: note === undefined ? {} : { note } }),
    onSuccess: async (count) => {
      queryClient.setQueryData(stockKeys.count(venueId, countId), count);
      await invalidate();
    },
  });
}

export function useCancelCount(organizationId: string, venueId: string, countId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({ mutationFn: () => api.cancelStockCount({ path: { organizationId, venueId, countId } }), onSuccess: invalidate });
}

export function usePutProductRecipes(organizationId: string, venueId: string, productId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({
    mutationFn: (body: PutStockProductRecipesRequest) => api.putStockProductRecipes({ path: { organizationId, venueId, productId }, body }),
    onSuccess: invalidate,
  });
}

export function useCopyFromVenue(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidateStock(organizationId, venueId);
  return useMutation({ mutationFn: (body: CopyStockFromVenueRequest) => api.copyStockFromVenue({ path: { organizationId, venueId }, body }), onSuccess: invalidate });
}

/**
 * What copying another venue's items and recipes would do: a dry run (`dryRun: true` writes nothing), so the preview
 * needs only this venue's `inventory:manage`, no read at the other venue. Re-read each time the dialog opens.
 */
export function useCopyPreview(organizationId: string, venueId: string, sourceVenueId: string | null) {
  const api = useApi();
  return useQuery({
    // Outside the venue's stock keys: the copy itself must not re-run its preview behind the answer it shows.
    queryKey: ['stock-copy-preview', venueId, sourceVenueId ?? 'none'] as const,
    enabled: sourceVenueId !== null,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    queryFn: () => api.copyStockFromVenue({ path: { organizationId, venueId }, body: { sourceVenueId: sourceVenueId ?? '', dryRun: true } }),
  });
}

export type { StockItemKind, StockLevel, StockMovementKind };
